import React, { memo, useContext } from 'react';
import { SocialIcon } from 'react-social-icons';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import { isReceiptPath } from '../../../../catalog/Receipt';
import { isValidObject, isValidString } from '../../../../catalog/Validity';
import { context } from '../../../../globalState/GlobalState';
import Footer1 from '../../../designs/uiElements/footer1/Footer1';
import { WebsiteDesignType } from '../../../../catalog/enums/WebsiteDesignType';
import { optimXStoreWebsite } from '../../../../catalog/TeamInfo';
import OptimXFooter from './OptimXFooter';
import AdFooter from './AdFooter';
import { getXURLFomTwitterURL } from '../../../../catalog/Social';
import { cdnImage, ImageWidth } from '../../../../catalog/ImageCdn';

const FOOTER_LINKS = [
    { title: 'Home', to: '/' },
    { title: 'Roster', to: '/roster' },
    { title: 'Articles', to: '/articles' },
    { title: 'Schedule', to: '/schedule/upcoming' },
];

const Footer = (props) => {
    const { showLinks } = props;
    const { globalState } = useContext(context);
    const location = useLocation();

    const footerTextColor = isValidObject(globalState) && isValidObject(globalState.websiteStyles) && isValidObject(globalState.websiteStyles.colors) && isValidString(globalState.websiteStyles.colors.fontColor) ? `${globalState.websiteStyles.colors.fontColor}` : '#ffffff';
    // Same color at ~35% opacity for the social circle outlines (8-digit hex works for #rrggbb colors).
    const footerBorderColor = /^#[0-9a-f]{6}$/i.test(footerTextColor) ? `${footerTextColor}59` : 'rgba(255, 255, 255, 0.35)';

    // Same silhouette logo as the navbar (it reads on the team-color background), falling back to the full logo.
    const getFooterLogo = () => {
        if (isValidObject(globalState.silhouetteLogo) && isValidString(globalState.silhouetteLogo.logo3)) {
            return globalState.silhouetteLogo.logo3;
        }
        if (isValidObject(globalState.silhouetteLogo) && isValidString(globalState.silhouetteLogo.logo2)) {
            return globalState.silhouetteLogo.logo2;
        }
        if (isValidObject(globalState.logo) && isValidString(globalState.logo.logo3)) {
            return globalState.logo.logo3;
        }
        return '';
    };

    // Social profile URLs in footer order (YouTube, X, Facebook, Instagram, TikTok, LinkedIn).
    const getSocialUrls = () => {
        if (!isValidObject(globalState) || !isValidObject(globalState.socials)) {
            return [];
        }
        const { socials } = globalState;
        return [
            socials.youtubeURL,
            isValidString(socials.twitterURL) ? getXURLFomTwitterURL(socials.twitterURL) : '',
            socials.facebookURL,
            socials.instagramURL,
            socials.tiktokURL,
            socials.linkedinURL,
        ].filter((url) => { return isValidString(url); });
    };

    const getFooterDesign = (children) => {
        if (!isValidObject(globalState) || !isValidObject(globalState.websiteStyles)) {
            return null;
        }

        if (globalState.websiteStyles.design.homepage === WebsiteDesignType.DESIGN_1) {
            return (
                <Footer1 showLinks={showLinks}>
                    {children}
                </Footer1>
            );
        }

        return (
            <Footer1 showLinks={showLinks}>
                {children}
            </Footer1>
        );
    };

    return (
        <>
            {getFooterDesign(
                <>
                    <div className="team-footer full-width" style={{ color: footerTextColor }}>
                        <div className="team-footer-inner">
                            <div className="team-footer-top">
                                {getFooterLogo() ? (
                                    <img src={cdnImage(getFooterLogo(), ImageWidth.LOGO)} className="team-footer-logo" alt={isValidString(globalState.teamName) ? globalState.teamName : 'Team Logo'} />
                                ) : null}
                                <div className="team-footer-name-container">
                                    <p className="team-footer-name m-t-0 m-b-0">{isValidString(globalState.teamName) ? globalState.teamName : globalState.teamAlias}</p>
                                    <span className="team-footer-underline" style={{ backgroundColor: footerTextColor }} />
                                </div>
                            </div>
                            <div className="team-footer-bottom">
                                <nav className="team-footer-links" aria-label="Footer">
                                    {showLinks && !optimXStoreWebsite ? FOOTER_LINKS.map((link) => {
                                        return (
                                            <NavLink key={link.to} className="team-footer-link text-no-underline" style={{ color: footerTextColor }} to={link.to} exact>{link.title}</NavLink>
                                        );
                                    }) : null}
                                </nav>
                                <div className="team-footer-social">
                                    {showLinks && !optimXStoreWebsite ? getSocialUrls().map((url) => {
                                        return (
                                            <span key={url} className="team-footer-social-icon" style={{ borderColor: footerBorderColor }}>
                                                <SocialIcon url={url} fgColor={footerTextColor} bgColor="transparent" target="_blank" style={{ width: 40, height: 40 }} />
                                            </span>
                                        );
                                    }) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                    {!isReceiptPath(location.pathname) ? <AdFooter /> : null}
                    <OptimXFooter />
                </>,
            )}
        </>
    );
};

Footer.propTypes = {
    showLinks: PropTypes.bool,
};

Footer.defaultProps = {
    showLinks: true,
};

export default memo(Footer);
